import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api, errorMessage, unwrap, type AuditEntry } from "../../api/client";
import { useSession } from "../../auth/session";
import { formatDateTime, fromParisInput } from "../../domain/format";
import { fr } from "../../i18n/fr";
import { Confirm } from "../../ui/components";

interface Filters { entityType?: string; action?: string; actor?: string; from?: string; to?: string }

/**
 * History of an event (SW-WS-40) or the global journal (admin) when `eventId` is absent. `onShow` tells whether
 * an entry still points at an object of the event and shows it on the map (SW-HI-03).
 */
export function HistoryPanel({ eventId, revision, onShow }: { eventId?: string; revision?: number; onShow?(entityType: string, entityId: string): boolean }) {
  const s = useSession();
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<number | undefined>();
  const [filters, setFilters] = useState<Filters>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchPage = async (cur?: number, reset = false) => {
    setBusy(true); setError(null);
    try {
      const query = { ...filters, cursor: cur, limit: 50 };
      const r = eventId
        ? unwrap(await api.GET("/events/{id}/history", { params: { path: { id: eventId }, query } }))
        : unwrap(await api.GET("/admin/audit", { params: { query } }));
      setItems((old) => (reset ? r.items : [...old, ...r.items]));
      setCursor(r.nextCursor);
    } catch (x) { setError(errorMessage(x, fr.errors)); } finally { setBusy(false); }
  };
  useEffect(() => { void fetchPage(undefined, true); }, [eventId, revision, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = async () => {
    if (!eventId) return;
    try {
      unwrap(await api.DELETE("/events/{id}/history", { params: { path: { id: eventId } } }));
      setConfirmClear(false);
      await fetchPage(undefined, true);
    } catch (x) { setError(errorMessage(x, fr.errors)); setConfirmClear(false); }
  };

  const actions = Object.keys(fr.history.actions).filter((a) => eventId ? !a.startsWith("USER_") && !["SETTINGS_UPDATED", "SYMBOLOGY_UPDATED", "PASSWORD_CHANGED"].includes(a) : true);

  return (
    <section className="stack">
      <div className="row between wrap">
        <h3>{eventId ? fr.history.title : fr.admin.audit.title}</h3>
        <div className="row">
          {eventId && <a className="button" href={`/api/v1/events/${encodeURIComponent(eventId)}/history.csv`} download>{fr.history.export}</a>}
          {eventId && s.can("data.delete") && <button className="danger ghost" onClick={() => setConfirmClear(true)}>{fr.history.deleteAll}</button>}
        </div>
      </div>
      <div className="row wrap small">
        <select value={filters.entityType ?? ""} onChange={(e) => setFilters({ ...filters, entityType: e.target.value || undefined })} aria-label={fr.history.filterType}>
          <option value="">{fr.history.filterType} : {fr.common.all.toLowerCase()}</option>
          {Object.entries(fr.history.entity).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.action ?? ""} onChange={(e) => setFilters({ ...filters, action: e.target.value || undefined })} aria-label={fr.history.filterAction}>
          <option value="">{fr.history.filterAction} : {fr.common.all.toLowerCase()}</option>
          {actions.map((a) => <option key={a} value={a}>{fr.history.actions[a]}</option>)}
        </select>
        <input placeholder={fr.history.filterActor} value={filters.actor ?? ""} onChange={(e) => setFilters({ ...filters, actor: e.target.value || undefined })} />
        <label className="row tight">{fr.history.from} <input type="datetime-local" aria-label={fr.history.from} onChange={(e) => setFilters({ ...filters, from: e.target.value ? fromParisInput(e.target.value) : undefined })} /></label>
        <label className="row tight">{fr.history.to} <input type="datetime-local" aria-label={fr.history.to} onChange={(e) => setFilters({ ...filters, to: e.target.value ? fromParisInput(e.target.value) : undefined })} /></label>
      </div>
      {error && <div className="error small">{error}</div>}
      {items.length === 0 && !busy && <p className="muted small">{fr.history.empty}</p>}
      <table className="table small history">
        <thead><tr><th>{fr.history.when}</th><th>{fr.history.actor}</th><th>{fr.history.action}</th><th>{fr.history.object}</th>{!eventId && <th>{fr.history.event}</th>}<th>{fr.history.revision}</th></tr></thead>
        <tbody>
          {items.map((it) => (
            <HistoryRow key={it.id} it={it} showEvent={!eventId} open={open === it.id} onToggle={() => setOpen(open === it.id ? null : it.id)} onShow={onShow} />
          ))}
        </tbody>
      </table>
      {cursor != null && <button disabled={busy} onClick={() => fetchPage(cursor)}>{fr.history.more}</button>}
      <Confirm open={confirmClear} title={fr.history.deleteAll} text={fr.history.deleteConfirm} danger confirmLabel={fr.common.delete} onConfirm={clear} onClose={() => setConfirmClear(false)} />
    </section>
  );
}

function HistoryRow({ it, showEvent, open, onToggle, onShow }: { it: AuditEntry; showEvent: boolean; open: boolean; onToggle(): void; onShow?(entityType: string, entityId: string): boolean }) {
  const hasDetails = it.before != null || it.after != null;
  const [gone, setGone] = useState(false);
  const showable = !!onShow && !!it.entityId && (it.entityType === "RESTRICTION" || it.entityType === "POI");
  return (
    <>
      <tr className={hasDetails ? "clickable" : ""} onClick={hasDetails ? onToggle : undefined}>
        <td className="nowrap">{formatDateTime(it.at)}</td>
        <td>{it.actor}</td>
        <td>{fr.history.actions[it.action] ?? it.action}</td>
        <td>
          {it.entityType ? `${fr.history.entity[it.entityType] ?? it.entityType} ` : ""}{it.entityLabel}
          {showable && (
            <button type="button" className="link small" title={fr.history.showOnMap} onClick={(e) => { e.stopPropagation(); setGone(!onShow!(it.entityType!, it.entityId!)); }}> ⌖</button>
          )}
          {gone && <span className="muted"> {fr.history.gone}</span>}
        </td>
        {showEvent && <td>{it.eventId ? <Link to={`/events/${it.eventId}/history`} onClick={(e) => e.stopPropagation()}>{it.eventId.slice(0, 8)}</Link> : ""}</td>}
        <td>{it.revision ?? ""}</td>
      </tr>
      {open && (
        <tr className="details"><td colSpan={showEvent ? 6 : 5}>
          <div className="grid2">
            <div><strong>{fr.history.before}</strong><pre>{it.before != null ? JSON.stringify(it.before, null, 1) : "—"}</pre></div>
            <div><strong>{fr.history.after}</strong><pre>{it.after != null ? JSON.stringify(it.after, null, 1) : "—"}</pre></div>
          </div>
        </td></tr>
      )}
    </>
  );
}
